import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { fileApi, ragApi } from "../services/api";

/**
 * Ambos ao nível do módulo, e não dentro do render: declarados lá dentro, cada
 * render criava um TIPO de componente novo, o React desmontava e remontava as
 * listas, e o foco saltava fora do botão que se acabou de usar — o que quebra a
 * navegação por teclado da única acção repetida destas listas.
 */
const SectionHeading = ({ title, count, hint, expanded, onToggle }) => (
  <div>
    <div className="flex items-baseline gap-2">
      {/* O <button> DENTRO do <h4> quando a secção abre e fecha, pela mesma razão
          do cabeçalho do painel: um <h4> dentro de um <button> é conteúdo inválido
          e tira o cabeçalho do outline de acessibilidade. */}
      <h4 className="text-sm font-semibold m-0" style={{ color: "var(--color-text-primary)" }}>
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex items-center gap-1.5 text-left font-semibold"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--color-text-muted)" }} />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--color-text-muted)" }} />
            )}
            {title}
          </button>
        ) : (
          title
        )}
      </h4>
      <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
        {count}
      </span>
    </div>
    {/* Em parágrafo que embrulha, e não truncado numa linha: a legenda das
        substituídas é a frase que este mecanismo existe para não esconder. */}
    {hint && (
      <p className="text-xs mt-1 max-w-3xl" style={{ color: "var(--color-text-muted)" }}>
        {hint}
      </p>
    )}
  </div>
);

const NamedList = ({ title, entries, hint, action, listAction, reasonLabel = (reason) => reason }) => {
  // Fechadas por omissão. Estas listas são o "o que aconteceu" e o "porque não",
  // que se consultam quando há uma pergunta — a fila é que é o trabalho, e com 3
  // excluídas mais 5 rejeitadas ficava enterrada debaixo de nomes que ninguém
  // estava a ler. A CONTAGEM fica no cabeçalho, portanto fechado continua a dizer
  // quantas são, que é o sinal; os nomes é que são o detalhe.
  const [expanded, setExpanded] = useState(false);
  if (entries.length === 0) return null;
  return (
    <div className="mt-6">
      <SectionHeading
        title={title}
        count={entries.length}
        // A legenda explica a lista, portanto vai com ela: fechada, explicaria
        // uma coisa que não está no ecrã.
        hint={expanded ? hint : undefined}
        expanded={expanded}
        onToggle={() => setExpanded((value) => !value)}
      />
      {/* Linhas separadas por uma régua em vez de espaçamento solto: com 27
          entradas, o nome e a etiqueta ficam alinhados em coluna. */}
      {/* Uma acção sobre a lista inteira vive DENTRO da secção aberta: fechada, a
          secção é uma contagem, e um botão ao lado de uma contagem não diz sobre o
          quê. */}
      {expanded && listAction && <div className="mt-2 flex justify-end">{listAction}</div>}
      {expanded && (
        <ul className="mt-2 divide-y" style={{ borderColor: "var(--color-border)" }}>
        {entries.map((entry) => (
          <li
            key={`${entry.promotion_id}-${entry.reason || ""}`}
            className="flex items-center justify-between gap-3 py-2 text-sm"
            style={{ color: "var(--color-text-secondary)" }}
          >
            <span className="truncate">{entry.name || entry.promotion_id}</span>
            <div className="flex items-center gap-3 shrink-0">
              {entry.reason && <span className="badge-inactive">{reasonLabel(entry.reason)}</span>}
              {action && action(entry)}
            </div>
          </li>
        ))}
      </ul>
      )}
    </div>
  );
};

/**
 * O que a VTEX tem e a lista de campanhas não consegue mostrar (RPAA-229).
 *
 * As campanhas sincronizadas já aparecem na lista de ficheiros como quaisquer
 * outras — este painel existe para as três coisas que uma lista não mostra: o que
 * está em fila de aprovação, o que ficou de fora e porquê, e quando foi a última
 * sincronização.
 *
 * O painel só se desenha quando o backend diz `is_vtex`. É ele que decide, e não o
 * frontend a adivinhar: todos os tenants têm página de campanhas, e isto não
 * significa nada num chat radio_popular, modz ou salsa.
 */
const VtexPromotionSync = ({ availableChats = [], onFilesChanged, filesChangedAt = 0 }) => {
  const { translate, language } = useLanguage();
  // O papel da coleção, e não admin: o backend passou a gatilhar os quatro
  // endpoints deste painel por assert_can_manage_collection("campaigns"), porque
  // quem aprova campanhas tem `campaigns` e nunca `admin`. Um gate de admin aqui
  // escondia os botões exactamente a quem os pode usar.
  const { canManageCollection } = useAuth();
  const canApprove = canManageCollection("campaigns");

  // Uma loja é um chat VTEX. O painel resolve a loja sozinho e nunca depende de um
  // filtro externo.
  //
  // Filtra pelo `platform_type` e não só pelo activo: este selector é um selector de
  // LOJAS, e um chat de voz ali é um beco — escolhe-se e o painel só sabe dizer que
  // aquilo não é uma loja. Sem lojas nenhumas não há painel; com UMA não há
  // selector, porque um selector de uma opção não é uma escolha.
  //
  // O `naoSabeAPlataforma` cobre a janela de rollout em que o backoffice já traz
  // este código e o backend ainda não devolve o campo: aí não se filtra nada e a
  // decisão volta a ser do `is_vtex` da vista, que é o comportamento anterior. Sem
  // isto, um deploy pela ordem errada fazia o painel desaparecer sem dizer porquê.
  const storeChats = useMemo(() => {
    const activos = (availableChats || []).filter((chat) => chat?.is_active && !chat?.internal_chat);
    const naoSabeAPlataforma = !activos.some((chat) => "platform_type" in chat);
    return naoSabeAPlataforma ? activos : activos.filter((chat) => chat.platform_type === "vtex");
  }, [availableChats]);
  const [chatId, setChatId] = useState(null);
  useEffect(() => {
    if (!chatId && storeChats.length > 0) setChatId(storeChats[0].id);
  }, [storeChats, chatId]);

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // As promoções à espera do servidor agora, por id, e qual a acção de cada uma.
  // Um mapa e não um slot só: com um slot, começar a segunda acção reabilitava os
  // botões da primeira a meio do voo, e um segundo clique aprovava-a duas vezes.
  //
  // Não é optimista de propósito: estas acções mudam o que o agente diz a clientes,
  // e às vezes o servidor leva um segundo. Ver o runOnPromotion.
  const [pending, setPending] = useState({});
  // Fechado por omissão: a página das campanhas é a lista de ficheiros, e o painel é
  // um sítio a que se vai. Fechado continua a dizer o que importa — a etiqueta com o
  // número por aprovar vive no cabeçalho e não aqui dentro.
  const [expanded, setExpanded] = useState(false);
  // As promoções escolhidas para uma acção em bloco, por id. Um Set e não uma
  // lista: a pergunta que se faz 52 vezes por render é "esta está escolhida?".
  const [selected, setSelected] = useState(() => new Set());
  // A acção em bloco à espera de confirmação, "approve" ou "reject". Em linha na
  // própria barra e não num modal: a selecção já foi deliberada caixa a caixa, e
  // um modal por cima disso é atrito sobre atrito. O que o passo acrescenta é
  // dizer QUANTAS das escolhidas têm condição, que é a parte que se erra.
  const [confirming, setConfirming] = useState(null);
  // A última leitura falhou. Não se confunde com "ainda não li" nem com "este chat
  // não é loja VTEX", porque as três levavam ao mesmo ecrã vazio e só uma delas se
  // resolve carregando outra vez.
  const [loadFailed, setLoadFailed] = useState(false);

  // O `view` é estado DA LOJA. Tudo o que possa sobreviver a uma troca de chat tem
  // de ser invalidado por ela: uma resposta lenta da loja A chegava depois de se ter
  // escolhido a B e pintava a vista de A por cima. Pior do que um número errado — as
  // linhas de A ficavam clicáveis sob a selecção de B, e aprovar atribuía o chat de
  // B a uma promoção de A.
  const requestedChat = useRef(null);
  const settleTimer = useRef(null);
  const pollTimer = useRef(null);
  // Há uma espera a decorrer, e o `waitForSync` visto de dentro do `load`. Os dois em
  // refs pela mesma razão: o `load` é um useCallback e o `translate` do contexto muda
  // de identidade a cada render, portanto pô-los nas dependências punha o painel a
  // reler-se sozinho sem parar.
  const waiting = useRef(false);
  const waitRef = useRef(null);
  // O painel ainda está no ecrã. A sondagem tem um `await` de permeio, e limpar o
  // temporizador no desmontar não cancela um pedido em voo: quando ele resolvia, o
  // `tick` continuava, reagendava-se, e no fim chamava o `onFilesChanged` do pai, que
  // enche a lista da colecção ACTIVA com os ficheiros de campanhas. Trocar de
  // colecção a meio de uma sincronização longa era suficiente.
  const montado = useRef(true);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!chatId) return;
      requestedChat.current = chatId;
      // Uma releitura silenciosa não acende o spinner: serve para o painel apanhar o
      // servidor depois de uma acção optimista, sem piscar por baixo das mãos de
      // quem está a trabalhar na lista.
      if (!quiet) setLoading(true);
      try {
        const data = await ragApi.getVtexPromotionSync(chatId);
        if (requestedChat.current !== chatId) return;
        setView(data);
        setLoadFailed(false);
        // Uma corrida que já estava a decorrer quando esta página abriu: do agendador,
        // ou de outra pessoa noutro separador. A espera é a mesma, e sem isto o painel
        // dizia "a sincronizar" e nunca mais se mexia, que é a promessa por cumprir
        // que este trabalho veio tirar.
        if (data.running && !waiting.current) {
          setSyncing(true);
          waitRef.current?.(data.synced_at);
        }
      } catch (err) {
        console.error("VtexPromotionSync - load failed:", err);
        // A vista NÃO se limpa numa falha: limpá-la escondia o painel inteiro, e com
        // ele o único botão de tentar outra vez. Fica o que se leu da última vez, com
        // o aviso por cima.
        if (requestedChat.current === chatId) setLoadFailed(true);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [chatId]
  );

  // Trocar de loja limpa a vista e cancela a reconciliação agendada pela anterior.
  // O ref acerta AQUI e não só quando o `load` seguinte arranca: entre os dois
  // efeitos cabe a resposta de uma acção da loja anterior.
  useEffect(() => {
    requestedChat.current = chatId;
    setView(null);
    setLoadFailed(false);
    setSelected(new Set());
    setConfirming(null);
    // O `pending` também é da loja. Ficava para trás na troca, e como o ficheiro é
    // partilhado a mesma promoção aparece nas duas filas: uma entrada da loja A
    // desactivava os botões dessa linha na loja B.
    setPending({});
    clearTimeout(settleTimer.current);
    // A espera é da loja anterior: quem trocou já não está à espera dela.
    clearTimeout(pollTimer.current);
    waiting.current = false;
    setSyncing(false);
  }, [chatId]);

  useEffect(() => {
    load();
  }, [load]);

  // A lista de baixo mexeu nos ficheiros: apagar uma campanha, atribuir-lhe um chat
  // pelo "Gerir Chats", apagar a pasta inteira. O painel conta a partir dos mesmos
  // ficheiros, portanto relê — silenciosamente, que quem fez a acção está a olhar
  // para a lista e não para aqui.
  //
  // Compara com o valor anterior em vez de correr a cada render do efeito: o `load`
  // muda de identidade quando se troca de loja, e sem isto essa troca fazia duas
  // leituras.
  const lastFilesChange = useRef(filesChangedAt);
  useEffect(() => {
    if (lastFilesChange.current === filesChangedAt) return;
    lastFilesChange.current = filesChangedAt;
    load({ quiet: true });
  }, [filesChangedAt, load]);

  // Cada acção acerta o painel localmente e agenda UMA reconciliação com o
  // servidor. Sem o atraso, aprovar dez seguidas eram dez releituras da vista e dez
  // refreshes da lista de ficheiros — que é o que fazia isto parecer lento.
  useEffect(
    () => () => {
      montado.current = false;
      clearTimeout(settleTimer.current);
      clearTimeout(pollTimer.current);
    },
    []
  );
  const scheduleSettle = useCallback(() => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      load({ quiet: true });
      onFilesChanged?.();
    }, 1200);
  }, [load, onFilesChanged]);

  /**
   * Corre uma acção sobre uma promoção com a UI a andar à frente do servidor.
   *
   * O `apply` é um updater e não um objecto: dois cliques no mesmo tick calculavam
   * ambos a partir do mesmo `view` e o segundo desfazia o primeiro.
   *
   * Se o pedido falhar não se tenta desfazer à mão — relê-se a vista, que é a única
   * fonte que não pode divergir do servidor.
   */
  const runOnPromotion = async (entry, { action, apply, request, successMessage, errorMessage, feito }) => {
    // A linha FICA até o servidor confirmar, com o botão clicado a rodar e os outros
    // desactivados. Era optimista antes — a linha saía no mesmo tick — e isso escondia
    // o tempo de espera e, quando o pedido falhava, fazia a linha reaparecer.
    const actingChat = chatId;
    setPending((state) => ({ ...state, [entry.promotion_id]: action }));
    try {
      const result = await request();
      // Trocar de loja a meio limpa o `view`; aplicar aqui rebentava no updater, ou
      // pior, acertava as contagens da loja errada.
      if (requestedChat.current !== actingChat) return;
      setView((state) => (state ? apply(state) : state));
      // Um 200 nem sempre é um sim. O endpoint de desfazer responde
      // `{success: false, status: "not_rejected"}` quando não havia nada a desfazer, e
      // isso saía com o visto verde de quem acabou de conseguir alguma coisa.
      const mensagem = typeof successMessage === "function" ? successMessage(result) : successMessage;
      (feito && !feito(result) ? toast : toast.success)(mensagem);
      scheduleSettle();
    } catch (err) {
      console.error("VtexPromotionSync - action failed:", err);
      toast.error(errorMessage);
      load({ quiet: true });
    } finally {
      setPending((state) => {
        const { [entry.promotion_id]: _done, ...rest } = state;
        return rest;
      });
    }
  };

  /** Esta promoção está à espera do servidor? E foi por causa de qual botão? */
  const isPending = (promotionId, action) =>
    promotionId in pending && (action === undefined || pending[promotionId] === action);

  /** Tira a promoção da fila e acerta as contagens do estado actual. */
  const withoutQueued = (state, promotionId, { publishedDelta = 0 } = {}) => ({
    ...state,
    current: {
      ...(state.current || {}),
      published: Math.max(0, (state.current?.published ?? 0) + publishedDelta),
      queued: Math.max(0, (state.current?.queued ?? 0) - 1),
    },
    queued: (state.queued || []).filter((item) => item.promotion_id !== promotionId),
  });

  /**
   * Espera pela corrida e relê o painel quando ela acabar.
   *
   * O POST devolve "started" e não espera por nada: a corrida é de fundo, e vai de
   * 3s a mais de dois minutos conforme os documentos que tenha de reescrever (o LLM
   * é 85% do tempo, uma chamada por documento). Nenhuma espera fixa serve, e mandar
   * carregar em Atualizar é pedir a quem está a olhar que adivinhe o número.
   *
   * Dois sinais, e são precisos os dois: o `running` vem do lock do job e diz "ainda
   * estou a correr", o `synced_at` vem do relatório e diz "acabei e escrevi". Uma
   * corrida que morra a meio larga o lock sem escrever relatório, e é só a diferença
   * entre os dois que a distingue de uma que correu bem.
   *
   * Função simples e não `useCallback` de propósito: o `translate` do contexto muda
   * de identidade a cada render, portanto isto dentro de uma cadeia de dependências
   * de hooks punha o painel a reler-se sozinho para sempre.
   */
  const waitForSync = (previousSyncedAt) => {
    const actingChat = chatId;
    const inicio = Date.now();
    // Acima do timeout de 300s que o job impõe à corrida, mais a folga de arranque.
    const limite = inicio + 7 * 60 * 1000;
    // Quanto se espera pelo lock antes de concluir que a corrida nem chegou a
    // arrancar. A tarefa de fundo arranca logo a seguir à resposta do POST, portanto
    // vinte segundos é folga larga e não um palpite apertado.
    const arranque = inicio + 20 * 1000;
    let viuACorrer = false;

    // Um só sítio a terminar a espera, porque são cinco as saídas: acabou, morreu sem
    // relatório, nunca arrancou, estourou o tecto, ou trocou-se de loja.
    const parar = (mensagem, correu = false) => {
      // Só mexe no estado se esta espera ainda for a da loja em causa. Um `tick` da
      // loja A que resolva depois de se ter trocado para B desarmava a espera de B, e
      // o `load` seguinte arrancava uma SEGUNDA espera para B: duas a escrever no
      // mesmo temporizador, uma órfã e incancelável, e dois toasts no fim.
      if (requestedChat.current !== actingChat) return;
      waiting.current = false;
      setSyncing(false);
      if (mensagem) (correu ? toast.success : toast.error)(mensagem);
    };

    waiting.current = true;

    const tick = async () => {
      if (!montado.current || requestedChat.current !== actingChat) return parar();
      let data = null;
      try {
        data = await ragApi.getVtexPromotionSync(actingChat);
      } catch (err) {
        // Uma leitura falhada não desiste da espera: a corrida continua lá, e o
        // painel volta a perguntar daqui a três segundos.
        console.error("VtexPromotionSync - poll failed:", err);
      }
      // Depois do `await`, e não só antes: é aqui que o painel pode já não existir.
      if (!montado.current || requestedChat.current !== actingChat) return parar();

      if (data) {
        setView(data);
        setLoadFailed(false);
        viuACorrer = viuACorrer || data.running;
        if (data.synced_at !== previousSyncedAt) {
          onFilesChanged?.();
          // O MESMO número que o painel mostra em "por aprovar", e não o do
          // relatório: o `totals.queued` conta as que a corrida deixou por aprovar,
          // que não é a fila (uma rejeitada entretanto já lá não está). Dois números
          // no mesmo ecrã a discordar valem menos do que um.
          const porAprovar = data.current?.queued ?? 0;
          return parar(
            porAprovar > 0
              ? translate("vtexSyncFinished", { count: porAprovar })
              : translate("vtexSyncFinishedNothing"),
            true
          );
        }
        // Largou o lock sem escrever relatório, ou nunca chegou a tomá-lo. Nos dois
        // casos não há corrida nenhuma pela qual esperar, e dizê-lo é melhor do que
        // deixar o botão a rodar sete minutos.
        if ((viuACorrer && !data.running) || (!viuACorrer && Date.now() > arranque)) {
          return parar(translate("vtexSyncFinishedWithoutReport"));
        }
      }

      if (Date.now() > limite) return parar(translate("vtexSyncStillRunning"));
      pollTimer.current = setTimeout(tick, 3000);
    };

    pollTimer.current = setTimeout(tick, 1500);
  };

  waitRef.current = waitForSync;

  const handleSync = async () => {
    setSyncing(true);
    const previousSyncedAt = view?.synced_at ?? null;
    try {
      await ragApi.syncVtexPromotions(chatId);
      toast.success(translate("vtexSyncStarted"));
      waitForSync(previousSyncedAt);
    } catch (err) {
      console.error("VtexPromotionSync - sync failed:", err);
      toast.error(translate("vtexSyncFailed"));
      setSyncing(false);
    }
  };

  const handleApprove = (entry) =>
    runOnPromotion(entry, {
      action: "approve",
      // Sai da fila e passa a estar a funcionar, no instante do clique. Os totais da
      // última corrida NÃO se tocam: são o resumo dessa corrida, não uma contagem.
      apply: (state) => withoutQueued(state, entry.promotion_id, { publishedDelta: 1 }),
      // Aprovar é atribuir o chat ao ficheiro. Este endpoint já invalida o cache da
      // coleção, portanto o agente passa a ler a campanha de imediato.
      request: () => fileApi.addChatToFile(entry.file_path, chatId, "campaigns"),
      successMessage: translate("vtexPromotionApproved"),
      errorMessage: translate("vtexPromotionApproveFailed"),
    });

  const handleReject = (entry) =>
    runOnPromotion(entry, {
      action: "reject",
      // Sai da fila e passa para as rejeitadas, para a decisão ficar visível sem
      // esperar pelo servidor.
      apply: (state) => ({
        ...withoutQueued(state, entry.promotion_id),
        rejected: [{ promotion_id: entry.promotion_id, name: entry.name }, ...(state.rejected || [])],
      }),
      // Apagar o ficheiro É rejeitar: o backend regista a rejeição sozinho e a
      // sincronização seguinte não o recria.
      request: () => fileApi.deleteFile(entry.file_path.split("/").pop(), "campaigns", chatId),
      successMessage: translate("vtexPromotionRejected"),
      errorMessage: translate("vtexPromotionRejectFailed"),
    });

  /**
   * A mesma acção sobre várias promoções, com a concorrência travada.
   *
   * Travada em 4 porque cada aprovação escreve no Qdrant E no Postgres: 52 de uma
   * vez não chegam mais cedo, só enchem o pool. Uma que falhe não trava as outras —
   * fica contada e a reconciliação do fim mostra o estado real.
   */
  const runOnMany = async (entries, { action, request, successMessage, apply }) => {
    const actingChat = chatId;
    setPending((state) => ({ ...state, ...Object.fromEntries(entries.map((e) => [e.promotion_id, action])) }));

    const fila = [...entries];
    const feitas = [];
    const falhadas = [];
    const trabalhador = async () => {
      while (fila.length) {
        const entry = fila.shift();
        try {
          await request(entry);
          feitas.push(entry);
        } catch (err) {
          console.error(`VtexPromotionSync - ${action} failed for ${entry.promotion_id}:`, err);
          falhadas.push(entry);
        }
      }
    };
    await Promise.all(Array.from({ length: 4 }, trabalhador));

    // Depois do guarda, e não antes: um lote da loja A a terminar apagava a selecção
    // que já se estava a fazer na loja B, e tirava do `pending` uma promoção que
    // estava em voo em B — reabilitando-lhe os botões a meio, que é o duplo clique
    // que este mapa existe para impedir.
    if (requestedChat.current !== actingChat) return;

    setPending((state) => {
      const rest = { ...state };
      entries.forEach((e) => delete rest[e.promotion_id]);
      return rest;
    });
    setSelected(new Set());
    setConfirming(null);

    // Só as que o servidor confirmou saem da fila. As que falharam ficam lá, que é
    // o que faz a lista continuar a ser a lista do que falta.
    if (feitas.length) {
      setView((state) => (state ? apply(state, feitas) : state));
      toast.success(successMessage(feitas.length));
    }
    if (falhadas.length) toast.error(translate("vtexBulkFailed", { count: falhadas.length }));
    scheduleSettle();
  };

  // Recebe as entradas em vez de as derivar: o `sortedQueued` é declarado abaixo, e
  // ler daqui só funciona porque isto corre no clique e não no render. Uma
  // armadilha para quem mover o código, e o argumento tira-a.
  // O que sai da fila, e para onde. Aprovar e rejeitar esvaziam a fila; aceitar de
  // novo mexe nas rejeitadas e na fila não, portanto não cabia numa função só.
  const afterQueueAction = (action) => (state, feitas) => {
    const feitasIds = new Set(feitas.map((e) => e.promotion_id));
    return {
      ...state,
      current: {
        ...(state.current || {}),
        published: Math.max(0, (state.current?.published ?? 0) + (action === "approve" ? feitas.length : 0)),
        queued: Math.max(0, (state.current?.queued ?? 0) - feitas.length),
      },
      queued: (state.queued || []).filter((item) => !feitasIds.has(item.promotion_id)),
      rejected:
        action === "reject"
          ? [...feitas.map((e) => ({ promotion_id: e.promotion_id, name: e.name })), ...(state.rejected || [])]
          : state.rejected,
    };
  };

  const handleBulk = (action, entries) => {
    if (!entries || entries.length === 0) return;
    if (action === "approve") {
      return runOnMany(entries, {
        action: "approve",
        request: (entry) => fileApi.addChatToFile(entry.file_path, chatId, "campaigns"),
        successMessage: (count) => translate("vtexBulkApproved", { count }),
        apply: afterQueueAction("approve"),
      });
    }
    return runOnMany(entries, {
      action: "reject",
      request: (entry) => fileApi.deleteFile(entry.file_path.split("/").pop(), "campaigns", chatId),
      successMessage: (count) => translate("vtexBulkRejected", { count }),
      apply: afterQueueAction("reject"),
    });
  };

  // O caminho de volta do lote. Rejeitar em lote é um clique e desfazê-lo eram 16,
  // que foi exactamente o que aconteceu a quem apagou a pasta inteira para recomeçar:
  // as 16 promoções ficaram rejeitadas e a fila seguinte veio vazia.
  const handleUndoAllRejections = (entries) =>
    runOnMany(entries, {
      action: "undo",
      request: (entry) => ragApi.acceptVtexPromotion(chatId, entry.promotion_id),
      successMessage: (count) => translate("vtexBulkRejectionsUndone", { count }),
      apply: (state, feitas) => {
        const feitasIds = new Set(feitas.map((e) => e.promotion_id));
        return { ...state, rejected: (state.rejected || []).filter((item) => !feitasIds.has(item.promotion_id)) };
      },
    });

  const handleUndoRejection = (entry) =>
    runOnPromotion(entry, {
      action: "undo",
      apply: (state) => ({
        ...state,
        rejected: (state.rejected || []).filter((item) => item.promotion_id !== entry.promotion_id),
      }),
      request: () => ragApi.acceptVtexPromotion(chatId, entry.promotion_id),
      successMessage: (result) =>
        result?.success ? translate("vtexRejectionUndone") : translate("vtexPromotionNotRejected"),
      feito: (result) => result?.success !== false,
      errorMessage: translate("vtexUndoRejectionFailed"),
    });

  // Antes da primeira leitura, ou num chat que não é loja VTEX, não há painel.
  // Antes da primeira leitura não há nada a desenhar. Uma leitura FALHADA já é
  // alguma coisa: fica a casca, com o aviso e o botão de reler — limpar tudo
  // escondia o painel e, com ele, a única forma de tentar outra vez.
  if (!chatId || (!view && !loadFailed)) return null;
  // Um chat que não é loja VTEX só apaga o painel quando não há outra loja para
  // escolher. O selector de loja vive aqui dentro: num tenant com um chat não-VTEX à
  // frente na lista, devolver null aqui tornava a loja VTEX inalcançável.
  const isStore = view?.is_vtex === true;
  if (!isStore && !loadFailed && storeChats.length <= 1) return null;

  const current = view?.current || {};
  const queued = view?.queued || [];
  // As de outro canal de venda já não chegam aqui: o backend conta-as e não as põe no
  // relatório. São de outras lojas, não mudam nunca e ninguém lhes faz nada, portanto
  // não são resposta a pergunta nenhuma que este painel faça.
  const excluded = view?.excluded || [];
  const rejected = view?.rejected || [];
  const resurrected = view?.resurrected || [];
  const overwritten = view?.overwritten || [];

  const formatSyncedAt = (seconds) => {
    if (!seconds) return translate("vtexNeverSynced");
    // Fixa a locale na língua da app, como o FileList faz: este painel existe em
    // parte para se ler esta data, e o formato do browser podia não ser o da pessoa.
    return new Date(seconds * 1000).toLocaleString(language === "pt" ? "pt-PT" : "en-GB");
  };

  // `translate` devolve a própria chave quando não a conhece, o que é o
  // comportamento certo aqui: uma condição ou um motivo que o backend passe a
  // devolver aparece em bruto em vez de desaparecer da vista.
  const reasonLabel = (reason) => translate(`vtexReason_${reason}`);
  const conditionLabel = (condition) => translate(`vtexCondition_${condition}`);

  // As SEM condição primeiro, e é a ordenação que faz a fila trabalhável: são as
  // que se aprovam sem pensar, ficam juntas em cima, e escolhem-se de uma vez. As
  // que têm condição afundam, que é onde se quer ler linha a linha.
  //
  // Estável dentro de cada grupo, portanto a ordem do servidor sobrevive: ela vem
  // da ordem da VTEX e é a mesma que se vê no admin deles.
  const sortedQueued = [...queued].sort((a, b) => (a.condition ? 1 : 0) - (b.condition ? 1 : 0));
  const semCondicao = sortedQueued.filter((e) => !e.condition);
  const escolhidas = sortedQueued.filter((e) => selected.has(e.promotion_id));
  const escolhidasComCondicao = escolhidas.filter((e) => e.condition).length;
  const todasEscolhidas = sortedQueued.length > 0 && escolhidas.length === sortedQueued.length;

  const toggleOne = (promotionId) =>
    setSelected((state) => {
      const next = new Set(state);
      if (next.has(promotionId)) next.delete(promotionId);
      else next.add(promotionId);
      return next;
    });

  return (
    // Uma FAIXA do cartão dos ficheiros, e não um cartão dentro dele. O contentor de
    // cima é um `rounded-xl overflow-hidden` com uma faixa de cabeçalho fechada por
    // uma linha; um cartão aqui punha uma segunda linha de 1px a milímetros dessa, com
    // cantos redondos a bater nos cantos direitos do de fora e um vão branco de 24px
    // por baixo. Duas molduras encaixadas, a mesma cor, a dizer a mesma coisa.
    //
    // Segue portanto o ritmo da faixa de cima, o mesmo `px-6 py-5`, e fecha-se com a
    // mesma linha. O cartão de fora é que continua a ser a moldura de tudo isto.
    //
    // Distingue-se por FUNDO e não por moldura: uma faixa `surface-raised` entre o
    // cabeçalho branco e a lista branca lê-se como uma zona de controlo, e não como
    // mais uma linha de conteúdo. Uma moldura fazia o efeito contrário, punha um
    // segundo cartão dentro do primeiro.
    //
    // A régua vertical de 3px só aparece quando há fila. É o único sítio do painel
    // onde a cor carrega informação em vez de decorar: parado, isto é uma faixa
    // calma; com trabalho por fazer, ganha um sinal que se vê sem ler nada. Se
    // estivesse sempre lá, deixava de querer dizer o que quer que fosse.
    <div
      className="px-6 py-5 border-b"
      style={{
        borderColor: "var(--color-border)",
        backgroundColor: "var(--color-surface-raised)",
        boxShadow: queued.length > 0 ? "inset 3px 0 0 var(--color-amber)" : undefined,
      }}
    >
      {/* Uma linha só, tudo centrado no mesmo eixo. O select usa o `field-input` da
          casa, que tem as mesmas métricas verticais dos botões (0.5rem de padding e
          0.875rem de fonte) — era daí que vinha o desalinhamento. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* O <button> DENTRO do <h3>: um <h3> dentro de um <button> é conteúdo
            inválido e tira o cabeçalho do outline de acessibilidade. */}
        <h3 className="text-base font-semibold min-w-0 m-0" style={{ color: "var(--color-text-primary)" }}>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            className="flex items-center gap-2 text-left min-w-0 font-semibold"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0" style={{ color: "var(--color-text-muted)" }} />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "var(--color-text-muted)" }} />
            )}
            <span className="truncate">{translate("vtexSyncTitle")}</span>
            {queued.length > 0 && (
              <span className="badge-amber shrink-0">{translate("vtexQueuedBadge", { count: queued.length })}</span>
            )}
          </button>
        </h3>

        <div className="flex items-center gap-2">
          {storeChats.length > 1 && (
            <select
              value={chatId}
              onChange={(event) => setChatId(event.target.value)}
              aria-label={translate("vtexStorePicker")}
              className="field-input w-auto"
            >
              {storeChats.map((chat) => (
                <option key={chat.id} value={chat.id}>
                  {chat.name}
                </option>
              ))}
            </select>
          )}
          {/* Só quem pode gerir campanhas, e só numa loja: mostrar isto a quem leva
              403, ou num chat que não sincroniza nada, é um beco. */}
          {canApprove && isStore && (
            <button
              type="button"
              onClick={handleSync}
              // Também quando a corrida é de outra pessoa ou do agendador: um segundo
              // clique não faz nada, que o lock recusa a segunda corrida, mas o botão
              // activo promete que faz.
              disabled={syncing || view?.running}
              className="btn-primary"
            >
              <RefreshCw className={`h-4 w-4 ${syncing || view?.running ? "animate-spin" : ""}`} />
              {syncing || view?.running ? translate("vtexSyncing") : translate("vtexSyncNow")}
            </button>
          )}
        </div>
      </div>

      {/* Fora do colapso, de propósito: um `synced_at` antigo é o que denuncia um
          scheduler parado, e a sincronização desligada é a explicação de a fila não
          se mexer. Escondidos atrás de um clique, os dois sinais deixavam de o ser. */}
      <div
        className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
        style={{ color: "var(--color-text-muted)" }}
      >
        {/* A corrida em curso ocupa o lugar da última: enquanto está a correr, a hora
            da anterior é a informação que menos interessa, e mostrá-la ao lado de um
            botão a rodar era o que fazia parecer que nada estava a acontecer. */}
        {isStore &&
          (syncing || view.running ? (
            <span className="inline-flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {translate("vtexSyncing")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {translate("vtexLastRun")}: {formatSyncedAt(view.synced_at)}
            </span>
          ))}
        {isStore && !view.sync_enabled && (
          <span className="inline-flex items-center gap-1.5" style={{ color: "var(--color-amber-text)" }}>
            <AlertCircle className="h-3.5 w-3.5" />
            {translate("vtexSyncDisabled")}
          </span>
        )}
        {/* A repetição vive AQUI e não num botão permanente no cabeçalho: o painel
            relê-se sozinho a cada acção e no fim de cada corrida, portanto um botão
            de recarregar sempre visível só dizia que isto podia estar desactualizado.
            Falhar a leitura é a única vez em que a pessoa tem mesmo de insistir. */}
        {loadFailed && (
          <span className="inline-flex items-center gap-1.5" style={{ color: "var(--color-amber-text)" }}>
            <AlertCircle className="h-3.5 w-3.5" />
            {translate("vtexViewLoadFailed")}
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="underline underline-offset-2 disabled:opacity-50"
            >
              {loading ? translate("loading") : translate("vtexViewLoadRetry")}
            </button>
          </span>
        )}
        {/* Raro agora que o selector só lista lojas: só se vê durante a janela de
            rollout, ou se a plataforma do chat mudar entre a leitura da lista e a
            da vista. Manda escolher outra só quando há selector para isso. */}
        {!isStore && !loadFailed && (
          <span className="inline-flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            {storeChats.length > 1 ? translate("vtexChatIsNotAStore") : translate("vtexChatIsNotAStoreAlone")}
          </span>
        )}
      </div>

      {expanded && isStore && (
        <>
          {/* O que existe AGORA. Vem dos ficheiros, portanto apagar um ficheiro ou
              uma pasta mexe nisto de imediato. */}
          <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
            {[
              // "Aprovadas" e não "a funcionar": o número conta DECISÕES, e uma
              // promoção aprovada pode ainda não estar a valer. As `scheduled` entram
              // de propósito na sincronização, para poderem ser aprovadas com
              // antecedência, e a data de conhecimento esconde-as do agente até ao
              // dia. "A funcionar" afirmava por elas o contrário.
              //
              // Quem quer saber se uma está mesmo a valer tem a etiqueta de cada
              // ficheiro na lista, que é onde essa pergunta é por promoção e não um
              // total: "Em curso", "Ainda não entrou em vigor", "Expirada".
              ["vtexCurrentApproved", current.published],
              ["vtexCurrentQueued", current.queued],
            ].map(([key, value]) => (
              <div key={key}>
                {/* Sem relatório, o backend não sabe QUAIS as promoções desta loja e
                    devolve zero nos dois números — e diz isso no `report_missing`. Um
                    "0" grande era a afirmação que esse campo existe para não deixar
                    fazer: uma loja com 47 campanhas a funcionar mostrava "0 aprovadas"
                    depois de um Redis reiniciado. Um travessão não afirma nada. */}
                <div className="text-2xl font-semibold leading-none" style={{ color: "var(--color-text-primary)" }}>
                  {view.report_missing ? "—" : value ?? 0}
                </div>
                <div className="text-xs mt-1.5" style={{ color: "var(--color-text-muted)" }}>
                  {translate(key)}
                </div>
              </div>
            ))}
            {/* O nome do canal, com o número atrás. O número sozinho escondia a
                diferença que mais importa: na loja da Delta o 1 é "TDH - PT" e o 2 é
                "Colaboradores Grupo Nabeiro", com preços de empregado. Configurado o
                2 por engano, isto dizia "canal 2" e ninguém dava por nada.
                O número FICA, porque é por ele que se compara com a configuração e
                com o admin da VTEX, e é ele que resta quando a VTEX não dá o nome. */}
            {view.sales_channel && (
              <div className="ml-auto text-xs" style={{ color: "var(--color-text-muted)" }}>
                {translate("vtexSalesChannel")}:{" "}
                {view.sales_channel_name
                  ? `${view.sales_channel_name} (${view.sales_channel})`
                  : view.sales_channel}
              </div>
            )}
          </div>

          {/* Aqui esteve a contabilidade da última corrida, "N sem alterações e N
              removidas". Era o vocabulário de quem escreveu a sincronização, não o de
              quem aprova campanhas, e o número que aparecia quase sempre dizia que
              nada tinha acontecido. O que essas duas contas tinham de aproveitável
              continua no log de cada corrida, que é onde se vai quando há uma
              pergunta a fazer-lhes. */}

          {/* A fila de aprovação: a lista que existe para ser trabalhada. */}
          <div className="mt-6">
            {/* Sem legenda: o título, a contagem e os dois botões de cada linha já
                dizem o que isto é. A regra que a legenda explicava — nada chega
                atribuído, e o agente só lê depois de aprovada — aprende-se no
                primeiro ciclo e é a própria lista que a demonstra. */}
            <SectionHeading title={translate("vtexQueuedTitle")} count={queued.length} />

            {view.report_missing ? (
              <p className="text-sm mt-3" style={{ color: "var(--color-text-muted)" }}>
                {translate("vtexQueueUnknown")}
              </p>
            ) : queued.length === 0 ? (
              <p className="text-sm mt-3" style={{ color: "var(--color-text-muted)" }}>
                {translate("vtexQueuedEmpty")}
              </p>
            ) : (
              <>
                {/* A barra de selecção. Vive acima da lista e não flutua: com 15
                    linhas a lista cabe num ecrã e meio, e uma barra fixa taparia a
                    última linha exactamente quando se chega a ela. */}
                {canApprove && (
                  // Sem caixa enquanto não há selecção. Eram três rectângulos
                  // encaixados uns nos outros — barra com moldura e fundo, cartão com
                  // moldura, e dentro dele o texto com fundo — e as três molduras eram
                  // a mesma linha de 1px a dizer coisas diferentes, ou seja nenhuma.
                  //
                  // Agora a caixa é o SINAL de que a barra tem acções: aparece quando
                  // há promoções escolhidas, e é a única coisa nova no ecrã nesse
                  // momento. Sem selecção, isto são dois controlos soltos sobre a
                  // lista, que é tudo o que são.
                  <div
                    className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 py-2 ${
                      escolhidas.length > 0 ? "rounded-lg border px-3" : ""
                    }`}
                    style={
                      escolhidas.length > 0
                        ? { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface-raised)" }
                        : undefined
                    }
                  >
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={todasEscolhidas}
                        ref={(el) => {
                          // O terceiro estado: escolhidas algumas mas não todas. Sem
                          // ele a caixa lê-se como "nenhuma" com 14 de 15 marcadas.
                          if (el) el.indeterminate = escolhidas.length > 0 && !todasEscolhidas;
                        }}
                        onChange={() =>
                          setSelected(todasEscolhidas ? new Set() : new Set(sortedQueued.map((e) => e.promotion_id)))
                        }
                        className="rounded border-[var(--color-border)]"
                      />
                      <span style={{ color: "var(--color-text-secondary)" }}>
                        {escolhidas.length > 0
                          ? translate(
                              escolhidas.length === 1 ? "vtexSelectedCountOne" : "vtexSelectedCount",
                              { count: escolhidas.length }
                            )
                          : translate("vtexSelectAll")}
                      </span>
                    </label>

                    {/* O atalho que faz a primeira corrida: as sem condição são as
                        que se aprovam sem ler, e numa loja real são a maioria. */}
                    {escolhidas.length === 0 && semCondicao.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelected(new Set(semCondicao.map((e) => e.promotion_id)))}
                        className="text-sm underline underline-offset-2"
                        style={{ color: "var(--color-text-secondary)" }}
                      >
                        {translate(
                          semCondicao.length === 1 ? "vtexSelectUnconditionalOne" : "vtexSelectUnconditional",
                          { count: semCondicao.length }
                        )}
                      </button>
                    )}

                    {escolhidas.length > 0 &&
                      (confirming ? (
                        <div className="flex flex-wrap items-center gap-2 ml-auto">
                          <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                            {confirming === "approve"
                              ? escolhidasComCondicao > 0
                                ? translate("vtexConfirmApproveConditional", {
                                    count: escolhidas.length,
                                    conditional: escolhidasComCondicao,
                                  })
                                : translate("vtexConfirmApprove", { count: escolhidas.length })
                              : translate("vtexConfirmReject", { count: escolhidas.length })}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleBulk(confirming, escolhidas)}
                            // 52 promoções a 4 de cada vez são vários segundos com o
                            // botão vivo. O segundo clique num lote de rejeição mandava
                            // apagar ficheiros que já tinham saído, e a pessoa levava um
                            // "N não foram feitas" a dizer que falhou o que correu bem.
                            disabled={escolhidas.some((entry) => isPending(entry.promotion_id))}
                            className="btn-primary"
                          >
                            {/* O ÚNICO preenchido da fila, e só depois de a pergunta
                                estar feita: aqui já não há ambiguidade sobre o que
                                acontece a seguir, e é o passo que se quer óbvio. */}
                            {translate("vtexConfirm")}
                          </button>
                          <button type="button" onClick={() => setConfirming(null)} className="btn-secondary">
                            {translate("cancel")}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 ml-auto">
                          {/* Mesmo peso que os da linha, de propósito. "Aprovar" com
                              dois pesos no mesmo ecrã lê-se como duas acções
                              diferentes, e é a mesma a âmbitos diferentes. A barra já
                              se destaca pela posição, pelo fundo e pela contagem —
                              não precisa de um botão cheio, e um cheio deixava o
                              "Sincronizar agora" a competir com ele. */}
                          <button type="button" onClick={() => setConfirming("approve")} className="btn-secondary">
                            <Check className="h-4 w-4" />
                            {translate("vtexApprove")}
                          </button>
                          <button type="button" onClick={() => setConfirming("reject")} className="btn-destructive">
                            <X className="h-4 w-4" />
                            {translate("vtexReject")}
                          </button>
                        </div>
                      ))}
                  </div>
                )}

              <ul className="mt-3 space-y-2">
                {sortedQueued.map((entry) => (
                  <li
                    key={entry.promotion_id}
                    className="rounded-lg border p-4"
                    style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" }}
                  >
                    {/* Nome e etiqueta na mesma linha dos botões, tudo no mesmo eixo.
                        Com o nome e a condição em linhas separadas, os botões ficavam
                        a flutuar contra um bloco de duas alturas. */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {canApprove && (
                          <input
                            type="checkbox"
                            checked={selected.has(entry.promotion_id)}
                            onChange={() => toggleOne(entry.promotion_id)}
                            aria-label={entry.name || entry.promotion_id}
                            className="rounded border-[var(--color-border)] shrink-0"
                          />
                        )}
                        {/* O nome interno da VTEX: é por ele que se casa esta linha
                            com o que se vê no admin da VTEX, o que é impossível a
                            partir de um uuid. */}
                        <span className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                          {entry.name || entry.promotion_id}
                        </span>
                        {/* A condição, que o backend já classifica e esta linha
                            deitava fora. É o que separa "oferta geral, aprovo já" de
                            "está presa a alguma coisa, tenho de ler" — sem ela as
                            duas são visualmente iguais e a fila lê-se toda.

                            A AUSÊNCIA de etiqueta é que é o sinal de oferta geral:
                            uma etiqueta "sem condição" em 11 de 15 linhas seria ruído
                            a marcar o caso normal. */}
                        {entry.condition && (
                          <span className="badge-amber shrink-0">{conditionLabel(entry.condition)}</span>
                        )}
                      </div>
                      {canApprove && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleApprove(entry)}
                            disabled={isPending(entry.promotion_id)}
                            // Secundário e não primário: quinze botões verdes cheios
                            // competiam com o "Sincronizar agora" do cabeçalho, que é
                            // o primário do painel. O primário em bloco vive agora na
                            // barra de selecção, onde há um só.
                            className="btn-secondary"
                          >
                            {isPending(entry.promotion_id, "approve") ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            {translate("vtexApprove")}
                          </button>
                          {/* Um X e não um caixote: rejeitar NÃO apaga o ficheiro.
                              O ficheiro é partilhado entre as lojas e sobrevive —
                              o que acontece é esta loja deixar de o publicar. Um
                              caixote prometia a remoção que o backend não faz, que é
                              a mesma coisa que a lista de ficheiros dizia errado
                              antes do `action: "rejected"`.

                              E o ✓ pede um ✕: aceito ou recuso são o par. Um ✓ ao
                              lado de um caixote emparelha uma aprovação com uma
                              eliminação, que não são opostos. */}
                          <button
                            type="button"
                            onClick={() => handleReject(entry)}
                            disabled={isPending(entry.promotion_id)}
                            className="btn-destructive"
                          >
                            {isPending(entry.promotion_id, "reject") ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                            {translate("vtexReject")}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* O texto exacto que o agente leria. Inteiro e não truncado: é
                        sobre ele que a decisão é tomada, e num fundo próprio para se
                        distinguir da moldura da linha. */}
                    <pre
                      className="mt-3 rounded-md p-3 text-xs leading-relaxed whitespace-pre-wrap font-sans"
                      style={{
                        color: "var(--color-text-secondary)",
                        backgroundColor: "var(--color-surface-raised)",
                      }}
                    >
                      {entry.text}
                    </pre>
                  </li>
                ))}
              </ul>
              </>
            )}
          </div>

          {/* Estas quatro listas são todas do relatório da última corrida, e não
              estado actual — a legenda de cada uma diz isso. */}
          <NamedList
            title={translate("vtexExcludedTitle")}
            hint={translate("vtexExcludedDescription")}
            entries={excluded}
            reasonLabel={reasonLabel}
          />

          <NamedList
            title={translate("vtexRejectedTitle")}
            hint={translate("vtexRejectedDescription")}
            entries={rejected}
            listAction={
              canApprove &&
              rejected.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleUndoAllRejections(rejected)}
                  disabled={rejected.some((entry) => isPending(entry.promotion_id))}
                  className="btn-secondary"
                >
                  <RotateCcw className="h-4 w-4" />
                  {translate("vtexUndoAllRejections", { count: rejected.length })}
                </button>
              )
            }
            action={(entry) =>
              canApprove && (
                <button
                  type="button"
                  onClick={() => handleUndoRejection(entry)}
                  disabled={isPending(entry.promotion_id)}
                  className="btn-secondary"
                >
                  {isPending(entry.promotion_id, "undo") ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  {translate("vtexUndoRejection")}
                </button>
              )
            }
          />

          <NamedList
            title={translate("vtexResurrectedTitle")}
            hint={translate("vtexResurrectedDescription")}
            entries={resurrected}
          />

          <NamedList
            title={translate("vtexOverwrittenTitle")}
            hint={translate("vtexOverwrittenDescription")}
            entries={overwritten}
          />
        </>
      )}
    </div>
  );
};

export default VtexPromotionSync;